import { ChevronUpIcon, DropIcon } from "@components/Icons";
import { PrimaryButton } from "@components/Buttons";
import TableWithPagination from "@components/Tables/TableWithPagination";
import queryKeys from "@configs/react-query/query-key";
import { showErrorToast } from "@configs/react-toastify/snackbar";
import { useMutationData } from "@hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import { formatDateTime } from "@utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ITEMS_PER_PAGE_KEY, DEFAULT_ITEMS_PER_PAGE } from "@constants/table";
import useItemsPerPage from "@hooks/useItemsPerPage";

// Import AddProjectModal để sử dụng trong modal  
import AddProjectModal from "@components/UI/AddProjectModal";

const ProductProjectsSection = ({
  title = "Product Projects List",
  className = "",
  productSeq,
  showAddButton = true,
  canEditProductDetail = false,
  onProjectAdded = null, // Callback when project is added
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { colors } = useTheme();
  const [isExpanded, setIsExpanded] = useState(true);
  const [allProjectsData, setAllProjectsData] = useState([]); // Store all data
  const [currentPage, setCurrentPage] = useState(1);
  const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(ITEMS_PER_PAGE_KEY.PRODUCT_PROJECTS_LIST, DEFAULT_ITEMS_PER_PAGE);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [isAddProjectModalOpen, setIsAddProjectModalOpen] = useState(false);

  // API call to get projects with verification for this product
  const { mutate: fetchProjectsWithVerification } = useMutationData(
    queryKeys.getProjectsWithVerification || "get_project_with_verification",
    { method: "POST" },
    {
      onSuccess: (response) => {
        if (response && typeof response === "object") {
          const data = response.object || [];
          setAllProjectsData(data); // Store all data
        } else {
          setAllProjectsData([]);
          setTotalData(0);
          setTotalPages(0);
        }
        setLoading(false);
      },
      onError: (error) => {
        console.error("Error fetching product projects:", error);
        showErrorToast(t("product.get-product-projects-error"));
        setAllProjectsData([]);
        setTotalData(0);
        setTotalPages(0);
        setLoading(false);
      },
    },
  );

  // Fetch projects when component mounts
  useEffect(() => {
    if (productSeq) {
      setCurrentPage(1);
      setLoading(true);

      // Build request body (similar to AssignProjectModal)
      const bodyRequest = {};
      
      if (productSeq) {
        bodyRequest.productSeq = parseInt(productSeq);
      }

      fetchProjectsWithVerification(bodyRequest);
    } else {
      setCurrentPage(1);
      setAllProjectsData([]);
      setTotalData(0);
      setTotalPages(0);
      setLoading(false);
    }
  }, [productSeq, fetchProjectsWithVerification]);

  // Update pagination info when data changes
  useEffect(() => {
    if (allProjectsData.length > 0) {
      const newTotalPages = Math.ceil(allProjectsData.length / itemsPerPage);
      setTotalData(allProjectsData.length);
      setTotalPages(newTotalPages);
      
      // Reset to page 1 if current page is out of bounds
      if (currentPage > newTotalPages && newTotalPages > 0) {
        setCurrentPage(1);
      }
    } else {
      setTotalData(0);
      setTotalPages(0);
    }
  }, [allProjectsData, itemsPerPage]);

  // Reset to page 1 when sorting changes
  useEffect(() => {
    if (sortConfig.key) {
      setCurrentPage(1);
    }
  }, [sortConfig]);

  const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
    handleItemsPerPageChange(newItemsPerPage);
    setCurrentPage(1); // Reset to page 1 when changing number of rows
  };

  const handleSort = (columnKey) => {
    let direction = "asc";
    if (sortConfig.key === columnKey && sortConfig.direction === "asc") {
      direction = "desc";
    } else if (sortConfig.key === columnKey && sortConfig.direction === "desc") {
      // Reset sort when clicking the same column for the third time
      setSortConfig({ key: null, direction: "asc" });
      return;
    }
    setSortConfig({ key: columnKey, direction });
  };

  const columns = ["projectId", "projectName", "projectManager", "createrId", "createdAt"];

  const columnConfig = {
    projectId: {
      title: "PROJECT ID",
      style: {
        width: "20%",
      },
      headerRender: () => t("project.project-id"),
      customRender: (value) => (
        <span className={`text-sm ${colors.textPrimary}`}>
          {value || "---"}
        </span>
      ),
    },
    projectName: {
      title: "PROJECT NAME",
      style: {
        width: "25%",
      },
      headerRender: () => t("project.project-name"),
      customRender: (value) => (
        <span className={`text-sm ${colors.textPrimary}`}>
          {value || "---"}
        </span>
      ),
    },
    projectManager: {
      title: "PROJECT MANAGER",
      style: {
        width: "20%",
      },
      headerRender: () => t("project.project-manager"),
      customRender: (value, row) => (
        <UserNameWithId name={row.projectManagerName} id={value || row.projectManager} />
      ),
    },
    createrId: {
      title: "CREATOR",
      style: {
        width: "15%",
      },
      headerRender: () => t("common.creator"),
      customRender: (value, row) => (
        <UserNameWithId name={row.createrName} id={value || row.createrId} />
      ),
    },
    createdAt: {
      title: "CREATED AT",
      style: {
        width: "20%",
      },
      headerRender: () => t("common.create-at"),
      customRender: (value) => (
        <span className={`text-sm ${colors.textPrimary}`}>
          {value ? formatDateTime(value) : "---"}
        </span>
      ),
    },
  };

  const toggleAccordion = () => {
    setIsExpanded(!isExpanded);
  };

  const handleAddProject = () => {
    setIsAddProjectModalOpen(true);
  };

  const getProjectSeq = (row) => {
    if (row?.projectSeq) return row.projectSeq;
    if (Array.isArray(row?.verificationDetail) && row.verificationDetail.length > 0) {
      return row.verificationDetail[0]?.projectSeq;
    }
    return null;
  };

  const handleRowClick = (row) => {
    if (!canEditProductDetail) return;
    const targetProjectSeq = getProjectSeq(row);
    if (targetProjectSeq) {
      navigate(`/projects/${targetProjectSeq}`);
    }
  };

  const handleCloseModal = () => {
    setIsAddProjectModalOpen(false);
  };

  const handleProjectAdded = () => {
    // Refresh projects data
    if (productSeq) {
      setLoading(true);
      const bodyRequest = {};
      if (productSeq) {
        bodyRequest.productSeq = parseInt(productSeq);
      }
      fetchProjectsWithVerification(bodyRequest);
    }
    
    // Close modal
    setIsAddProjectModalOpen(false);
    
    // Call parent callback if provided
    if (onProjectAdded) {
      onProjectAdded();
    }
  };

  // Sort and paginate data
  const sortedAndPaginatedData = React.useMemo(() => {
    let dataToSort = allProjectsData;
    
    // Apply sorting if enabled
    if (sortConfig.key && dataToSort.length) {
      dataToSort = [...dataToSort].sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        // Handle null/undefined values - always put them at the end
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return 1; // null values always go to the end
        if (bValue == null) return -1; // null values always go to the end

        // Convert to strings for comparison
        const aStr = String(aValue).toLowerCase().trim();
        const bStr = String(bValue).toLowerCase().trim();

        // Try numeric comparison first
        const aNum = parseFloat(aStr);
        const bNum = parseFloat(bStr);
        if (!isNaN(aNum) && !isNaN(bNum)) {
          const comparison = aNum - bNum;
          return sortConfig.direction === "asc" ? comparison : -comparison;
        }

        // Try date comparison
        const aDate = new Date(aStr);
        const bDate = new Date(bStr);
        if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
          const comparison = aDate.getTime() - bDate.getTime();
          return sortConfig.direction === "asc" ? comparison : -comparison;
        }

        // Default to string comparison
        const comparison = aStr.localeCompare(bStr);
        return sortConfig.direction === "asc" ? comparison : -comparison;
      });
    }

    // Apply pagination
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return dataToSort.slice(startIndex, endIndex);
  }, [allProjectsData, sortConfig, currentPage, itemsPerPage]);

  return (
    <div
      className={`rounded-2xl px-6 pt-6 pb-4 flex flex-col ${isExpanded && (allProjectsData.length === 0 || !productSeq) ? "h-48" : ""} ${colors.bgPrimary} ${colors.borderPrimary} ${className}`}
    >
      <div className={`mb-2 flex items-center justify-between`}>
        <div
          className={`flex cursor-pointer items-center`}
          tabIndex={0}
          role="button"
          aria-label={isExpanded ? "Collapse Product Projects Section" : "Expand Product Projects Section"}
          onClick={toggleAccordion}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleAccordion();
            }
          }}
        >
          <ChevronUpIcon
            className={`mr-2 h-5 w-5 transition-transform duration-200 ${colors.textSecondary} ${!isExpanded ? "rotate-180" : ""}`}
          />
          <h2 className={`text-md font-semibold ${colors.textPrimary}`}>{title}</h2>
        </div>
        
        {showAddButton && (
          <div onClick={(e) => e.stopPropagation()}>
            <PrimaryButton
              onClick={handleAddProject}
              size="md"
            >
              {t("project.add-new-project")}
            </PrimaryButton>
          </div>
        )}
      </div>
      {isExpanded && (
        <>
          <div
            className={`pt-1 ${allProjectsData.length === 0 && !loading ? "flex-1 flex items-center justify-center" : ""}`}
          >
            {(allProjectsData.length === 0 && !loading) || !productSeq ? (
              <div className="flex flex-col items-center justify-center">
                <div className="mb-4">
                  <DropIcon className={`h-16 w-16 ${colors.textActive}`}/>     
                </div>
                <p className={`${colors.textSecondary} text-sm`}>
                  {t("product.no-projects-assigned-to-product")}
                </p>
              </div>
            ) : (
              <TableWithPagination
                columns={columns}
                data={sortedAndPaginatedData}
                columnConfig={columnConfig}
                loading={loading}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalPages={totalPages}
                totalData={totalData}
                maxRowsPerPage={itemsPerPage}
                onItemsPerPageChange={handleItemsPerPageChangeWithReset}
                rowKey="projectId"
                showPagination={true}
                shouldShowGoToPage={true}
                hasCheckbox={false}
                showItemsPerPageSelector={true}
                onSort={handleSort}
                sortConfig={sortConfig}
                onRowClick={canEditProductDetail ? handleRowClick : undefined}
              />
            )}
          </div>
        </>
      )}
      
      {/* Add Project Modal */}
      <AddProjectModal
        isOpen={isAddProjectModalOpen}
        onClose={handleCloseModal}
        productSeq={productSeq}
        onProjectAdded={handleProjectAdded}
      />
    </div>
  );
};

export default ProductProjectsSection;